import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { KnowledgeNote } from '../../hooks/useKnowledge'
import { KindBadge } from './KindBadge'

/**
 * The notes that apply to an asset without belonging to it: its ancestors' and
 * the organization's (spec 2026-10-10-06). Read-only on purpose: an inherited
 * note is edited where it lives, so each entry links to its source.
 */
export function InheritedKnowledge({ org, notes }: { org: string; notes: KnowledgeNote[] }) {
    const { t } = useTranslation('knowledge')
    if (notes.length === 0) return null

    return (
        <div className="mt-4 space-y-2" data-testid="inherited-knowledge">
            <h4 className="text-sm font-medium text-muted-foreground">{t('inherited_title')}</h4>
            <ul className="space-y-2">
                {notes.map((note) => (
                    <li
                        key={note.uid}
                        className="rounded-md border border-border bg-muted/30 p-3"
                        data-testid={`inherited-note-${note.uid}`}
                    >
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-foreground" data-testid="inherited-note-title">
                                {note.title}
                            </span>
                            <KindBadge kind={note.kind} />
                        </div>
                        {note.text_content && (
                            <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-sm text-muted-foreground">
                                {note.text_content}
                            </p>
                        )}
                        <div className="mt-1 text-xs text-muted-foreground">
                            {note.source.type === 'asset' && note.source.asset_uid ? (
                                <Link
                                    to="/$org/assets/$assetUid"
                                    params={{ org, assetUid: note.source.asset_uid }}
                                    className="text-primary hover:underline"
                                    data-testid={`inherited-note-source-${note.uid}`}
                                >
                                    {t('inherited_from_asset', { name: note.source.asset_name ?? '' })}
                                </Link>
                            ) : (
                                <Link
                                    to="/$org/knowledge"
                                    params={{ org }}
                                    search={{ q: undefined, kind: undefined }}
                                    className="text-primary hover:underline"
                                    data-testid={`inherited-note-source-${note.uid}`}
                                >
                                    {t('inherited_from_organization')}
                                </Link>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    )
}
